import { format, standardize } from '@updater-components/phone-formatter';
import creditCardType from 'credit-card-type';

const ssnMask = value => {
  const unmasked =
    value && value.replace ? value.replace(/[\D\s]/g, '').substring(0, 9) : '';

  return !unmasked?.length
    ? ''
    : unmasked.length <= 3
    ? unmasked
    : unmasked.length <= 5
    ? `${unmasked.slice(0, 3)}-${unmasked.slice(3)}`
    : `${unmasked.slice(0, 3)}-${unmasked.slice(3, 5)}-${unmasked.slice(5, 9)}`;
};

const longDateMask = value => {
  if (!value || !value.replace) {
    return '';
  }

  const digits = value.replace(/[\D\s]/g, '').substring(0, 8);

  if (digits.length === 1 && digits[0] > 1) {
    return `0${digits[0]}`;
  }

  if (digits.length === 3 && digits[2] > 3) {
    return `${digits.slice(0, 2)}/0${digits[2]}`;
  }

  return digits.length <= 2
    ? digits
    : digits.length <= 4
    ? `${digits.slice(0, 2)}/${digits.slice(2, 4)}`
    : `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4, 8)}`;
};

const shortDateMask = value => {
  if (!value || !value.replace) {
    return '';
  }

  const digits = value.replace(/[\D\s]/g, '').substring(0, 4);

  if (digits.length === 1 && digits[0] > 1) {
    return `0${digits[0]}`;
  }

  return digits.length <= 2
    ? digits
    : `${digits.slice(0, 2)}/${digits.slice(2, 4)}`;
};

/*
 * get: extracts the unmasked value from a masked value
 * set: applies the mask to an unmasked value
 */
const masks = {
  noop: {
    get: value => value,
    set: value => value,
  },
  phone: {
    get: value => standardize(value),
    set: value => {
      const unmasked = masks.phone.get(value);
      return unmasked.length ? format(unmasked) : '';
    },
    placeholder: '(212) 867-5309',
    cursorPosition: ({ length }) => {
      if (length <= 3) {
        return length + 1;
      } else if (length <= 6) {
        return length + 3;
      } else {
        return length + 4;
      }
    },
  },
  socialSecurityNumber: {
    get: ssnMask, // Keep the mask in the value
    set: ssnMask,
    placeholder: 'XXX-XX-XXXX',
    cursorPosition: ({ length }) =>
      length <= 3 ? length : length <= 5 ? length + 1 : length + 2,
  },
  longDate: {
    get: longDateMask, // Keep the mask in the value
    set: longDateMask,
    placeholder: 'MM/DD/YYYY',
    cursorPosition: value => masks.longDate.set(value).length,
  },
  shortDate: {
    get: shortDateMask, // Keep the mask in the value
    set: shortDateMask,
    placeholder: 'MM/YY',
    cursorPosition: value => masks.shortDate.set(value).length,
  },
  creditCardNumber: {
    get: value => (value && value.replace ? value.replace(/[\D\s]/g, '') : ''),
    set: value => {
      const number = masks.creditCardNumber.get(value);

      const types = creditCardType(number);
      if (types.length !== 1) {
        return number;
      }

      const { gaps, lengths } = types[0];
      // Cap the number at the maximum valid length
      const digits = number.split('').slice(0, Math.max(...lengths));

      gaps.forEach((gap, index) =>
        digits.length > gap + index
          ? digits.splice(gap + index, 0, ' ')
          : digits
      );

      return digits.join('');
    },
    placeholder: 'Credit card number',
    cursorPosition: value => masks.creditCardNumber.set(value).length,
  },
};

export default masks;
